/* eslint-disable react/display-name */
import React from 'react'
import { Typography } from 'antd'
import { utcToZonedTime, format } from 'date-fns-tz'
import { filterXSS } from 'xss'
import { IUserLogs } from '../../types/custom-queries'
import IntlMessages from '../../util/IntlMessages'
import { EventTypeEnum } from '@vacationtracker/shared/types/core-event'
import { IDecoratorWords } from '../../routes/Reports/CompanyLogsPage'
import WorkWeek from '../work-week'
import dayjs from 'dayjs'
const { Text } = Typography
import { dayOfWeekMap } from '../../constants'
import { HourFormatEnum } from '@vacationtracker/shared/types/user'
import { getAverageWorkingHours } from '@vacationtracker/shared/functions/work-week'
import { roundTo2Decimals } from '@vacationtracker/shared/functions/calculation-shared'
import getDateInUserTimezone from '@vacationtracker/shared/functions/get-date-in-user-timezone'
import { QuotaUnits } from '@vacationtracker/shared/types/leave-policy'
import lowerCase from 'lodash/lowerCase'
import upperCase from 'lodash/upperCase'
import { findSubdivision } from '@vacationtracker/shared/data/countries'
import { getAddonsEvents } from '@vacationtracker/shared/common/addons-events'
import { SubscriptionPlan } from '@vacationtracker/shared/types/company'
import { AttributionFormatMessage, formatApiClientAttribution } from '@vacationtracker/shared/public-api/attribution'
import { ILogApiClient } from '@vacationtracker/shared/types/logs'
import { PublicApiKeyRevocationCode } from '@vacationtracker/shared/public-api/types'
import { KeyOutlined } from '@ant-design/icons'

interface ICompanyLogLine extends IUserLogs {
  timezone: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  paramsJson: any
  decoratorWords: IDecoratorWords
  hourFormat: HourFormatEnum
  formatMessage: Function
  index: number
  companyPlan?: SubscriptionPlan
}

type Periods = {
  amount: number
  years: number
  units: QuotaUnits
}[]

export interface ICompanyLogLineResult {
  key: string
  color: string
  children: string | JSX.Element
  label: string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const decorateResponse = (value: any) => {
  if (value === '') {
    return words.notSet
  }
  if (typeof value == 'boolean') {
    return value ? words.yes : words.no
  }
  return value ?? words.notSet
}

const decorateMonth = (monthNumber: number, withDay?: number) => {
  if (!monthNumber) {
    return words.notSet
  }
  const monthName = dayjs()
    .month(monthNumber - 1)
    .locale('en-US')
    .format('MMMM')
    .toLowerCase()
  return (
    <>
      <IntlMessages id={`app.${monthName}`}></IntlMessages>
      {withDay ? ' ' : ''}
      {withDay}
    </>
  )
}

const decorateDay = (dayNumber: number) => {
  if (!dayNumber) {
    return words.notSet
  }
  const dayName = dayjs().day(dayNumber).locale('en-US').format('dddd').toLowerCase()
  return <IntlMessages id={`app.${dayName}`}></IntlMessages>
}

const decorateDate = (date: Date) => {
  if (!date) {
    return words.notSet
  }
  return dayjs(date).format('MM-DD-YYYY')
}

const decorateEntitlementPeriods = (periodIntervals: Periods) => {
  //period intervals are given in the following form
  //"periods":[{"days":2,"years":5},{"days":3,"years":10}]
  //we need to convert it to the following form
  //2 days every 5 years, 3 days every 10 years
  let result = ''
  periodIntervals.forEach((periodInterval, index: number) => {
    if (index > 0) {
      result += ', '
    }
    const unitWord = periodInterval.amount === 1 ? (periodInterval.units === 'hours' ? words.hour : words.day) : periodInterval.units === 'hours' ? words.hours : words.days

    result += `${periodInterval.amount} ${unitWord} ${words.every} ${periodInterval.years} ${lowerCase(words.years)}`
  })
  return result
}

let words: IDecoratorWords

// Key events logged before keys had their own name only carry the integration name.
const publicApiKeyLogName = (params: Record<string, unknown>): string => String(params.name ?? params.integrationName ?? '')

const publicApiKeyMessageIds: Record<string, string> = {
  [EventTypeEnum.PUBLIC_API_KEY_CREATED]: 'components.logs.publicApiKeyCreated',
  [EventTypeEnum.PUBLIC_API_KEY_UPDATED]: 'components.logs.publicApiKeyUpdated',
  [EventTypeEnum.PUBLIC_API_KEY_ROTATED]: 'components.logs.publicApiKeyRotated',
}

const revocationReasonMessageIds: Record<PublicApiKeyRevocationCode, string> = {
  manual: 'publicApi.revocation.reason.manual',
  owner_deactivated: 'publicApi.revocation.reason.ownerDeactivated',
  owner_deleted: 'publicApi.revocation.reason.ownerDeleted',
  owner_not_admin: 'publicApi.revocation.reason.ownerNotAdmin',
  owner_location_admin_reduced: 'publicApi.revocation.reason.ownerLocationAdminReduced',
  owner_approver_reduced: 'publicApi.revocation.reason.ownerApproverReduced',
  owner_permissions_reduced: 'publicApi.revocation.reason.ownerPermissionsReduced',
  company_deleted: 'publicApi.revocation.reason.companyDeleted',
}

const publicApiWebhookMessageIds: Record<string, string> = {
  [EventTypeEnum.PUBLIC_API_WEBHOOK_CREATED]: 'components.logs.publicApiWebhookCreated',
  [EventTypeEnum.PUBLIC_API_WEBHOOK_UPDATED]: 'components.logs.publicApiWebhookUpdated',
  [EventTypeEnum.PUBLIC_API_WEBHOOK_DELETED]: 'components.logs.publicApiWebhookDeleted',
}

export const getLogLine = ({
  decoratorWords,
  type,
  timestamp,
  creatorName = '<Unknown>',
  creatorId,
  paramsJson,
  timezone,
  hourFormat,
  formatMessage,
  companyPlan,
  index,
}: ICompanyLogLine): ICompanyLogLineResult | null => {
  if (creatorName === 'system') {
    creatorName = formatMessage({ id: 'app.system' })
  }
  const actingAsName = creatorName
  const apiClient: ILogApiClient | undefined = paramsJson?.apiClient?.keyId ? paramsJson.apiClient : undefined
  if (apiClient?.name) {
    creatorName = formatApiClientAttribution({ name: creatorName, integrationName: apiClient.name, formatMessage: formatMessage as AttributionFormatMessage })
  }

  words = decoratorWords

  const dateInTimezone = utcToZonedTime(new Date(timestamp), timezone)
  const dateTimeFormat = `MMM d, yyyy ${hourFormat === HourFormatEnum.twentyFour ? 'HH' : 'h'}:mm${hourFormat === HourFormatEnum.twentyFour ? '' : ' a'}`
  const time = format(dateInTimezone, dateTimeFormat, { timeZone: timezone })

  let text: string | JSX.Element = ''
  let color = 'blue'

  const setLogs = () => {
    const aiAssistantCreatorNameDecorator = formatMessage({ id: 'ai.assisted.onboarding.companyLogUserNameDecorator' })
    const decoratedCreatorNameAIAssisted = decorateUserName(creatorName, aiAssistantCreatorNameDecorator)
    try {
      switch (type) {
        case EventTypeEnum.COMPANY_CREATED:
          text = (
            <IntlMessages
              id="components.logs.companyCreated"
              values={{
                name: filterXSS(paramsJson.name as string),
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.COMPANY_SETTINGS_UPDATED:
          text = (
            <IntlMessages
              id="components.logs.companySettingsUpdated"
              values={{
                user: creatorName,
                announceNewUsers: decorateResponse(paramsJson.announceNewUsers),
                hourlyLeaveAccounting: decorateResponse(paramsJson.hourlyLeaveAccounting),
                userEndDate: decorateResponse(paramsJson.userEndDate),
                calculationStartYear: decorateResponse(paramsJson.calculationStartYear),
                importUsersAutomatically: decorateResponse(paramsJson.importUsersAutomatically),
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.BILLING_UPDATED:
          text = (
            <IntlMessages
              id="components.logs.billingUpdated"
              values={{
                paymentProcessor: paramsJson.paymentProcessor === 'microsoft-billing' ? 'Microsoft' : paramsJson.paymentProcessor,
                br: () => <br />,
              }}
            />
          )
          break
        case EventTypeEnum.BILLING_CANCELLED:
          text = <IntlMessages id="components.logs.billingCancelled" values={{ paymentProcessor: paramsJson.paymentProcessor, br: () => <br /> }} />
          break
        case EventTypeEnum.SUBSCRIPTION_CHANGED:
          text = (
            <IntlMessages
              id="components.logs.subscriptionChanged"
              values={{
                plan: paramsJson.plan,
                period: paramsJson.subscriptionPeriod,
                paymentProcessor: paramsJson.paymentProcessor,
                status: paramsJson.subscriptionStatus,
                seats: Number(paramsJson.seats) || 0,
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.SUBSCRIPTION_CANCELED:
          color = 'red'
          text = (
            <IntlMessages
              id="components.logs.subscriptionCancelled"
              values={{
                reason: paramsJson.cancelReason,
                comment: paramsJson.cancelReasonComment,
                user: creatorName,
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.IMPORT_USERS:
          text = (
            <IntlMessages
              id="components.logs.importUsers"
              values={{
                user: creatorName,
                totalUsers: paramsJson.importAllUsers ? paramsJson.totalUsers : paramsJson.users.length,
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.IMPORT_LEAVES:
          text = (
            <IntlMessages
              id="components.logs.importLeaves"
              values={{
                user: creatorName,
                totalLeaves: paramsJson.totalLeaves,
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.LEAVE_POLICY_DISABLED:
          color = 'red'
          text = (
            <IntlMessages
              id="components.logs.leavePolicyDisabled"
              values={{
                user: paramsJson.source === 'ai-onboarding-assistant' ? decoratedCreatorNameAIAssisted : creatorName,
                leaveTypeName: paramsJson.leaveTypeName,
                locationName: paramsJson.locationName,
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.LEAVE_POLICY_ENABLED:
          color = 'green'
          text = (
            <IntlMessages
              id="components.logs.leavePolicyEnabled"
              values={{
                user: paramsJson.source === 'ai-onboarding-assistant' ? decoratedCreatorNameAIAssisted : creatorName,
                leaveTypeName: paramsJson.leaveTypeName,
                locationName: paramsJson.locationName,
                daysPerYear: paramsJson.quotaSetIn === 'hours' ? roundTo2Decimals(paramsJson.hoursPerYear as number) : roundTo2Decimals(paramsJson.daysPerYear as number),
                maxRolloverDays:
                  paramsJson.quotaSetIn === 'hours' ? roundTo2Decimals(paramsJson.maxRolloverHours as number) : roundTo2Decimals(paramsJson.maxRolloverDays as number),
                units: paramsJson.quotaSetIn === 'hours' ? <IntlMessages id="app.hours" /> : <IntlMessages id="app.days" />,
                isApprovalRequired: decorateResponse(paramsJson.isApprovalRequired),
                hasUnlimitedDays: decorateResponse(paramsJson.hasUnlimitedDays),
                shortestLeaveInterval: paramsJson.shortestLeaveInterval,
                isReasonRequired: decorateResponse(paramsJson.isReasonRequired),
                accrualType: paramsJson.accrualType,
                negativeBallanceAllowed: decorateResponse(paramsJson.negativeBallanceAllowed),
                allowLeaveInPast: decorateResponse(paramsJson.allowLeaveInPast),
                hideLeaveType: decorateResponse(paramsJson.hideLeaveType),
                enableRolloverExpiry: decorateResponse(paramsJson.broughtForwardExpirationSettings.enabled),
                rolloverExpiryDate: decorateMonth(paramsJson.broughtForwardExpirationSettings.month as number, paramsJson.broughtForwardExpirationSettings.day as number),
                rolloverExpiryAfterDays: decorateResponse(paramsJson.broughtForwardExpirationSettings.afterDays),
                accrualCapRate: paramsJson.accrualCapRate ?? 0,
                toil: decorateResponse(paramsJson.toil),
                toilShortestInterval: decorateResponse(paramsJson.toilShortestInterval),
                toilRequestsAllowedForUsers: decorateResponse(paramsJson.toilRequestsAllowedForUsers),
                toilExpiration: decorateResponse(paramsJson.toilExpiration),
                toilExpirationDate: decorateResponse(paramsJson.toilExpirationDate),
                toilExpirationMonths: decorateResponse(paramsJson.toilExpirationMonths && paramsJson.toilExpirationMonths > 0 ? paramsJson.toilExpirationMonths : undefined),
                negativeBroughtForwardAllowed: decorateResponse(paramsJson.negativeBroughtForwardAllowed),
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.LEAVE_TYPE_CREATED:
          color = 'green'
          text = (
            <IntlMessages
              id="components.logs.leaveTypeCreated"
              values={{
                user: paramsJson.source === 'ai-onboarding-assistant' ? decoratedCreatorNameAIAssisted : creatorName,
                color: paramsJson.color,
                name: filterXSS(paramsJson.name as string),
                locations: paramsJson.locations?.join(', '),
                daysPerYear: paramsJson.leavePolicy.daysPerYear,
                maxRolloverDays: paramsJson.leavePolicy.maxRolloverDays,
                isApprovalRequired: decorateResponse(paramsJson.leavePolicy.isApprovalRequired),
                hasUnlimitedDays: decorateResponse(paramsJson.leavePolicy.hasUnlimitedDays),
                shortestLeaveInterval: paramsJson.leavePolicy.shortestLeaveInterval,
                isReasonRequired: decorateResponse(paramsJson.leavePolicy.isReasonRequired),
                accrualType: decorateResponse(paramsJson.leavePolicy.accrualType),
                negativeBallanceAllowed: decorateResponse(paramsJson.leavePolicy.negativeBallanceAllowed),
                allowLeaveInPast: decorateResponse(paramsJson.leavePolicy.allowLeaveInPast),
                hideLeaveType: decorateResponse(paramsJson.leavePolicy.hideLeaveType),
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.LEAVE_TYPE_UPDATED:
          if (!paramsJson.isActive) {
            color = 'red'
          }
          text = (
            <IntlMessages
              id="components.logs.leaveTypeUpdated"
              values={{
                user: paramsJson.source === 'ai-onboarding-assistant' ? decoratedCreatorNameAIAssisted : creatorName,
                name: filterXSS(paramsJson.name as string),
                color: paramsJson.color,
                isActive: decorateResponse(paramsJson.isActive),
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.LEAVE_TYPE_DELETED:
          color = 'red'
          text = (
            <IntlMessages
              id="components.logs.leaveTypeDeleted"
              values={{
                user: paramsJson.source === 'ai-onboarding-assistant' ? decoratedCreatorNameAIAssisted : creatorName,
                name: filterXSS(paramsJson.name as string),
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.LOCATION_CREATED:
          color = 'green'
          text = (
            <IntlMessages
              id="components.logs.locationCreated"
              values={{
                user: paramsJson.source === 'ai-onboarding-assistant' ? decoratedCreatorNameAIAssisted : creatorName,
                name: filterXSS(paramsJson.name as string),
                yearStartMonth: decorateMonth(paramsJson.yearStartMonth as number),
                yearStartDay: paramsJson.yearStartDay,
                isDefault: decorateResponse(paramsJson.isDefault),
                firstDayOfWeek: decorateDay(paramsJson.firstDayOfWeek as number),
                resetQuotas: paramsJson.resetQuotas.split('_').join(' '),
                rolloverExpiryMonth: decorateMonth(paramsJson.rolloverExpiryMonth as number),
                rolloverExpiryDay: decorateResponse(paramsJson.rolloverExpiryDay),
                rolloverExpiryAfterDays: decorateResponse(paramsJson.rolloverExpiryAfterDays),
                timezone: paramsJson.timezone,
                users: paramsJson.users.join(', ') || 'Not set',
                days: <WorkWeek days={paramsJson.days} firstDayOfWeek={paramsJson.firstDayOfWeek} separator=", " />,
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.LOCATION_UPDATED:
          text = (
            <IntlMessages
              id="components.logs.locationUpdated"
              values={{
                user: paramsJson.source === 'ai-onboarding-assistant' ? decoratedCreatorNameAIAssisted : creatorName,
                name: filterXSS(paramsJson.name as string),
                yearStartMonth: decorateMonth(paramsJson.yearStartMonth as number),
                yearStartDay: paramsJson.yearStartDay,
                isDefault: decorateResponse(paramsJson.isDefault),
                firstDayOfWeek: decorateDay(paramsJson.firstDayOfWeek as number),
                resetQuotas: paramsJson.resetQuotas,
                rolloverExpiryMonth: decorateMonth(paramsJson.rolloverExpiryMonth as number),
                rolloverExpiryDay: decorateResponse(paramsJson.rolloverExpiryDay),
                rolloverExpiryAfterDays: decorateResponse(paramsJson.rolloverExpiryAfterDays),
                timezone: paramsJson.timezone,
                users: paramsJson.users.join(', ') || 'Not set',
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.LOCATION_DELETED:
          text = (
            <IntlMessages
              id="components.logs.locationDeleted"
              values={{
                user: paramsJson.source === 'ai-onboarding-assistant' ? decoratedCreatorNameAIAssisted : creatorName,
                br: () => <br />,
              }}
            />
          )
          break
        case EventTypeEnum.LOCATION_WORK_WEEK_UPDATED:
          text = (
            <IntlMessages
              id="components.logs.locationWorkWeekUpdated"
              values={{
                user: paramsJson.source === 'ai-onboarding-assistant' ? decoratedCreatorNameAIAssisted : creatorName,
                name: paramsJson.name,
                hours: getAverageWorkingHours(paramsJson.hours), //Change this once we implement location working hours per day - at the moment each day has the same working hours
                days: <WorkWeek days={paramsJson.days} firstDayOfWeek={paramsJson.firstDayOfWeek || 1} separator=", " />,
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.LOCATION_YEAR_HOLIDAYS_CHANGED:
          text = (
            <IntlMessages
              id="components.logs.locationYearHolidaysChanged"
              values={{
                user: creatorName,
                name: filterXSS(paramsJson.name as string),
                year: paramsJson.year,
                holidays: paramsJson.holidays
                  .map(
                    (h) =>
                      `${h.name}: ${getDateInUserTimezone(h.date as string).toDateString()}
              ${h.isHalfDay ? `(${formatMessage({ id: 'app.halfDay' }) as string})` : ''}`
                  )
                  .join(', '),
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.LOCATION_HOLIDAYS_IMPORTED:
          text = (
            <IntlMessages
              id="components.logs.locationYearHolidaysImported"
              values={{
                user: creatorName,
                name: paramsJson.locationName,
                year: paramsJson.year,
                total: paramsJson.holidays,
                country: upperCase(paramsJson.country as string),
                // Subdivisions are stored as ISO 3166-2 now, and upperCase() would split 'DE-BY'
                // into 'DE BY'. Show the name where we can resolve one.
                state: decorateResponse(findSubdivision(String(paramsJson.state ?? ''))?.name ?? upperCase(paramsJson.state as string)),
                recurring: decorateResponse(paramsJson.recurring),
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.LOCATION_HOLIDAY_CREATED:
          text = (
            <IntlMessages
              id="components.logs.locationHolidayCreated"
              values={{
                user: creatorName,
                name: paramsJson.locationName,
                holidayName: paramsJson.name,
                occurrence: paramsJson.recurringRules?.specificDaySet ? (
                  <>
                    {Number(paramsJson.recurringRules?.specificDaySet?.[0])}. {decorateDay(Number(paramsJson.recurringRules?.specificDaySet?.[1]))}{' '}
                    {decorateMonth(Number(paramsJson.recurringRules?.specificDaySet?.[2]))}
                  </>
                ) : (
                  (() => {
                    const startDateStr = decorateDate(new Date(paramsJson.startDate as string))
                    const endDateStr = decorateDate(new Date(paramsJson.endDate as string))
                    return startDateStr === endDateStr ? startDateStr : `${startDateStr} - ${endDateStr}`
                  })()
                ),
                isHalfDay: decorateResponse(paramsJson.isHalfDay),
                recurring: decorateResponse(paramsJson.recurring),
                transferIfFallsOnNonWorkingDay: decorateResponse(paramsJson.transferIfFallsOnNonWorkingDay),
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.LOCATION_HOLIDAY_UPDATED:
          text = (
            <IntlMessages
              id="components.logs.locationHolidayUpdated"
              values={{
                user: creatorName,
                name: paramsJson.locationName,
                holidayName: paramsJson.name,
                recurring: decorateResponse(paramsJson.recurring),
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.LOCATION_HOLIDAY_DELETED:
          text = (
            <IntlMessages
              id="components.logs.locationHolidayDeleted"
              values={{
                user: creatorName,
                name: paramsJson.locationName,
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.LOCATION_HOLIDAYS_DELETED:
          text = (
            <IntlMessages
              id="components.logs.locationHolidaysDeleted"
              values={{
                user: creatorName,
                name: filterXSS(paramsJson.locationName as string),
                count: Number(paramsJson.count) || 0,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.LOCATION_YEAR_ROLLOVER:
          text = (
            <IntlMessages
              id="components.logs.locationYearRollover"
              values={{ name: filterXSS(paramsJson.name as string), br: () => <br />, b: (...chunks) => <Text strong>{chunks}</Text> }}
            />
          )
          break
        case EventTypeEnum.NOTIFICATION_CREATED:
          color = 'green'
          text = (
            <IntlMessages
              id={paramsJson.frequency === 'DAILY' ? 'components.logs.notificationDetailsDaily' : 'components.logs.notificationDetailsMonthly'}
              values={{
                action: 'created',
                user: creatorName,
                name: filterXSS(paramsJson.name as string),
                frequency: paramsJson.frequency,
                enabled: decorateResponse(paramsJson.enabled),
                locations: decorateResponse(paramsJson.locations?.join(', ')),
                teams: decorateResponse(paramsJson.teams.join(', ')),
                labels: decorateResponse(paramsJson.labels.join(', ')),
                firstDayOfWeek: <IntlMessages id={paramsJson.firstDayOfWeek === 0 ? 'app.sunday' : 'app.monday'} />,
                timezone: paramsJson.timezone,
                groupBy: paramsJson.groupBy,
                currentWeek: decorateResponse(paramsJson.currentWeek),
                sendEmpty: decorateResponse(paramsJson.sendEmpty),
                minute: paramsJson.minute,
                hour: paramsJson.hour,
                locale: paramsJson.locale,
                day: paramsJson.day && <IntlMessages id={dayOfWeekMap.find((day) => day.value === paramsJson.day)?.name} />,
                sendOnDays: paramsJson.sendOnDays ? (
                  <WorkWeek days={paramsJson.sendOnDays} firstDayOfWeek={paramsJson.firstDayOfWeek || 1} separator=", " />
                ) : (
                  <IntlMessages id="app.notSet" />
                ),
                destinations: paramsJson.destinations
                  .map((d) => {
                    if (d.platform === 'email') {
                      return `${d.platform}(s) ${d.addresses?.join(', ')}`
                    }
                    const platform = d.platform.replace(/^\w/, (s) => s.toUpperCase())
                    return `${platform} channel`
                  })
                  .join(' & '),
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.NOTIFICATION_UPDATED:
          text = (
            <IntlMessages
              id={paramsJson.frequency === 'DAILY' ? 'components.logs.notificationDetailsDaily' : 'components.logs.notificationDetailsMonthly'}
              values={{
                action: 'updated',
                user: creatorName,
                name: filterXSS(paramsJson.name as string),
                frequency: paramsJson.frequency,
                enabled: decorateResponse(paramsJson.enabled),
                locations: decorateResponse(paramsJson.locations?.join(', ')),
                teams: decorateResponse(paramsJson.teams.join(', ')),
                labels: decorateResponse(paramsJson.labels.join(', ')),
                firstDayOfWeek: <IntlMessages id={paramsJson.firstDayOfWeek === 0 ? 'app.sunday' : 'app.monday'} />,
                timezone: paramsJson.timezone,
                groupBy: paramsJson.groupBy,
                currentWeek: decorateResponse(paramsJson.currentWeek),
                sendEmpty: decorateResponse(paramsJson.sendEmpty),
                minute: paramsJson.minute,
                hour: paramsJson.hour,
                locale: paramsJson.locale,
                day: paramsJson.day && <IntlMessages id={dayOfWeekMap.find((day) => day.value === paramsJson.day)?.name} />,
                sendOnDays: paramsJson.sendOnDays ? (
                  <WorkWeek days={paramsJson.sendOnDays} firstDayOfWeek={paramsJson.firstDayOfWeek || 1} separator=", " />
                ) : (
                  <IntlMessages id="app.notSet" />
                ),
                destinations: paramsJson.destinations
                  .map((d) => {
                    if (d.platform === 'email') {
                      return `${d.platform}(s) ${d.addresses?.join(', ')}`
                    }
                    const platform = d.platform.replace(/^\w/, (s) => s.toUpperCase())
                    return `${platform} channel`
                  })
                  .join(' & '),
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.NOTIFICATION_DELETED:
          color = 'red'
          text = <IntlMessages id="components.logs.notificationDeleted" values={{ user: creatorName, br: () => <br /> }} />
          break
        case EventTypeEnum.TEAM_APPROVER_STATUS_CHANGED:
          text = (
            <IntlMessages
              id="components.logs.teamApproverStatusChanged"
              values={{
                user: creatorName,
                approvers: paramsJson.approvers.join(', '),
                team: paramsJson.teamName,
                br: () => <br />,
              }}
            />
          )
          break
        case EventTypeEnum.TEAM_CREATED:
          color = 'green'
          text = (
            <IntlMessages
              id="components.logs.teamCreated"
              values={{
                user: paramsJson.source === 'ai-onboarding-assistant' ? decoratedCreatorNameAIAssisted : creatorName,
                team: filterXSS(paramsJson.name as string),
                isDefault: decorateResponse(paramsJson.isDefault),
                users: decorateResponse(paramsJson.users.join(', ')),
                approvers: decorateResponse(paramsJson.approvers.join(', ')),
                parentDepartment: decorateResponse(paramsJson.parentDepartment),
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.TEAM_UPDATED:
          text = (
            <IntlMessages
              id="components.logs.teamUpdated"
              values={{
                user: paramsJson.source === 'ai-onboarding-assistant' ? decoratedCreatorNameAIAssisted : creatorName,
                team: filterXSS(paramsJson.name as string),
                isDefault: decorateResponse(paramsJson.isDefault),
                users: decorateResponse(paramsJson.users.join(', ')),
                approvers: decorateResponse(paramsJson.approvers.join(', ')),
                parentDepartment: decorateResponse(paramsJson.parentDepartment),
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.TEAM_DELETED:
          text = (
            <IntlMessages
              id="components.logs.teamDeleted"
              values={{
                user: paramsJson.source === 'ai-onboarding-assistant' ? decoratedCreatorNameAIAssisted : creatorName,
                br: () => <br />,
              }}
            />
          )
          break
        case EventTypeEnum.BLACKOUT_PERIOD_CREATED:
          color = 'green'
          text = (
            <IntlMessages
              id="components.logs.blackoutPeriodCreated"
              values={{
                user: creatorName,
                name: filterXSS(paramsJson.name as string),
                enabled: decorateResponse(paramsJson.isActive),
                recurring: decorateResponse(paramsJson.recurring),
                startDate: decorateDate(new Date(paramsJson.startDate as string)),
                endDate: decorateDate(new Date(paramsJson.endDate as string)),
                leaveTypes: paramsJson.leaveTypes?.join(', '),
                locations: Array.isArray(paramsJson.locations) ? paramsJson.locations?.join(', ') : paramsJson.locations,
                teams: Array.isArray(paramsJson.teams) ? paramsJson.teams?.join(', ') : paramsJson.teams,
                labels: Array.isArray(paramsJson.labels) ? paramsJson.labels?.join(', ') : paramsJson.labels,
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.BLACKOUT_PERIOD_UPDATED:
          color = 'green'
          text = (
            <IntlMessages
              id="components.logs.blackoutPeriodUpdated"
              values={{
                user: creatorName,
                name: filterXSS(paramsJson.name as string),
                enabled: decorateResponse(paramsJson.isActive),
                recurring: decorateResponse(paramsJson.recurring),
                startDate: decorateDate(new Date(paramsJson.startDate as string)),
                endDate: decorateDate(new Date(paramsJson.endDate as string)),
                leaveTypes: paramsJson.leaveTypes?.join(', '),
                locations: Array.isArray(paramsJson.locations) ? paramsJson.locations?.join(', ') : paramsJson.locations,
                teams: Array.isArray(paramsJson.teams) ? paramsJson.teams?.join(', ') : paramsJson.teams,
                labels: Array.isArray(paramsJson.labels) ? paramsJson.labels?.join(', ') : paramsJson.labels,
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.BLACKOUT_PERIOD_DELETED:
          color = 'red'
          text = (
            <IntlMessages
              id="components.logs.blackoutPeriodDeleted"
              values={{
                user: creatorName,
                name: filterXSS(paramsJson.automationName as string),
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.LEAVE_REQUEST_NOTICE_PERIOD_CREATED:
          color = 'green'
          text = (
            <IntlMessages
              id="components.logs.leaveRequestNoticePeriodCreated"
              values={{
                user: creatorName,
                name: filterXSS(paramsJson.name as string),
                enabled: decorateResponse(paramsJson.isActive),
                minLeaveDurationForAdvanceNotice: paramsJson.minLeaveDurationForAdvanceNotice,
                requiredAdvanceNoticeDays: paramsJson.requiredAdvanceNoticeDays,
                autoRejectResponse: paramsJson.autoRejectResponse,
                leaveTypes: paramsJson.leaveTypes?.join(', '),
                locations: Array.isArray(paramsJson.locations) ? paramsJson.locations?.join(', ') : paramsJson.locations,
                teams: Array.isArray(paramsJson.teams) ? paramsJson.teams?.join(', ') : paramsJson.teams,
                labels: Array.isArray(paramsJson.labels) ? paramsJson.labels?.join(', ') : paramsJson.labels,
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.LEAVE_REQUEST_NOTICE_PERIOD_UPDATED:
          color = 'green'
          text = (
            <IntlMessages
              id="components.logs.leaveRequestNoticePeriodUpdated"
              values={{
                user: creatorName,
                name: filterXSS(paramsJson.name as string),
                enabled: decorateResponse(paramsJson.isActive),
                minLeaveDurationForAdvanceNotice: paramsJson.minLeaveDurationForAdvanceNotice,
                requiredAdvanceNoticeDays: paramsJson.requiredAdvanceNoticeDays,
                autoRejectResponse: paramsJson.autoRejectResponse,
                leaveTypes: paramsJson.leaveTypes?.join(', '),
                locations: Array.isArray(paramsJson.locations) ? paramsJson.locations?.join(', ') : paramsJson.locations,
                teams: Array.isArray(paramsJson.teams) ? paramsJson.teams?.join(', ') : paramsJson.teams,
                labels: Array.isArray(paramsJson.labels) ? paramsJson.labels?.join(', ') : paramsJson.labels,
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.LEAVE_REQUEST_NOTICE_PERIOD_DELETED:
          color = 'red'
          text = (
            <IntlMessages
              id="components.logs.leaveRequestNoticePeriodDeleted"
              values={{
                user: creatorName,
                name: filterXSS(paramsJson.automationName as string),
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.MAXIMUM_USERS_AWAY_CREATED:
          color = 'green'
          text = (
            <IntlMessages
              id="components.logs.teamMaximumUsersAwayCreated"
              values={{
                ...getMaximumUsersAwayValues(paramsJson),
                user: creatorName,
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.MAXIMUM_USERS_AWAY_UPDATED:
          color = 'green'
          text = (
            <IntlMessages
              id="components.logs.teamMaximumUsersAwayUpdated"
              values={{
                ...getMaximumUsersAwayValues(paramsJson),
                user: creatorName,
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.MAXIMUM_USERS_AWAY_DELETED:
          color = 'red'
          text = (
            <IntlMessages
              id="components.logs.teamMaximumUsersAwayDeleted"
              values={{
                user: creatorName,
                name: filterXSS(paramsJson.automationName as string),
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.PROBATION_PERIOD_CREATED:
          color = 'green'
          text = (
            <IntlMessages
              id="components.logs.probationPeriodCreated"
              values={{
                user: creatorName,
                name: filterXSS(paramsJson.name as string),
                enabled: decorateResponse(paramsJson.isActive),
                lengthDays: paramsJson.lengthDays,
                leaveTypes: paramsJson.leaveTypes?.join(', '),
                locations: Array.isArray(paramsJson.locations) ? paramsJson.locations?.join(', ') : paramsJson.locations,
                teams: Array.isArray(paramsJson.teams) ? paramsJson.teams?.join(', ') : paramsJson.teams,
                labels: Array.isArray(paramsJson.labels) ? paramsJson.labels?.join(', ') : paramsJson.labels,
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.PROBATION_PERIOD_UPDATED:
          color = 'green'
          text = (
            <IntlMessages
              id="components.logs.probationPeriodUpdated"
              values={{
                user: creatorName,
                name: filterXSS(paramsJson.name as string),
                enabled: decorateResponse(paramsJson.isActive),
                lengthDays: paramsJson.lengthDays,
                leaveTypes: paramsJson.leaveTypes?.join(', '),
                locations: Array.isArray(paramsJson.locations) ? paramsJson.locations?.join(', ') : paramsJson.locations,
                teams: Array.isArray(paramsJson.teams) ? paramsJson.teams?.join(', ') : paramsJson.teams,
                labels: Array.isArray(paramsJson.labels) ? paramsJson.labels?.join(', ') : paramsJson.labels,
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.PROBATION_PERIOD_DELETED:
          color = 'red'
          text = (
            <IntlMessages
              id="components.logs.probationPeriodDeleted"
              values={{
                user: creatorName,
                name: filterXSS(paramsJson.automationName as string),
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.SENIORITY_ENTITLEMENT_CREATED:
          color = 'green'
          text = (
            <IntlMessages
              id="components.logs.seniorityEntitlementCreated"
              values={{
                user: creatorName,
                name: filterXSS(paramsJson.name as string),
                enabled: decorateResponse(paramsJson.isActive),
                prorated: decorateResponse(paramsJson.prorate),
                calculateFrom: (...chunks) => (paramsJson.calculateFrom === 'USER_EMPLOYMENT_DATE' ? chunks : ''),
                startDate: () => (paramsJson.calculateFrom !== 'USER_EMPLOYMENT_DATE' ? decorateDate(new Date(timestamp)) : ''),
                leaveType: paramsJson.leaveType,
                locations: Array.isArray(paramsJson.locations) ? paramsJson.locations?.join(', ') : paramsJson.locations,
                teams: Array.isArray(paramsJson.teams) ? paramsJson.teams?.join(', ') : paramsJson.teams,
                labels: Array.isArray(paramsJson.labels) ? paramsJson.labels?.join(', ') : paramsJson.labels,
                entitlementPeriods: decorateEntitlementPeriods(paramsJson.periods as Periods),
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.SENIORITY_ENTITLEMENT_UPDATED:
          color = 'green'
          text = (
            <IntlMessages
              id="components.logs.seniorityEntitlementUpdated"
              values={{
                user: creatorName,
                name: filterXSS(paramsJson.name as string),
                enabled: decorateResponse(paramsJson.isActive),
                prorated: decorateResponse(paramsJson.prorate),
                calculateFrom: (...chunks) => (paramsJson.calculateFrom === 'USER_EMPLOYMENT_DATE' ? chunks : ''),
                startDate: () => (paramsJson.calculateFrom !== 'USER_EMPLOYMENT_DATE' ? decorateDate(new Date(timestamp)) : ''),
                leaveType: paramsJson.leaveType,
                locations: Array.isArray(paramsJson.locations) ? paramsJson.locations?.join(', ') : paramsJson.locations,
                teams: Array.isArray(paramsJson.teams) ? paramsJson.teams?.join(', ') : paramsJson.teams,
                labels: Array.isArray(paramsJson.labels) ? paramsJson.labels?.join(', ') : paramsJson.labels,
                entitlementPeriods: decorateEntitlementPeriods(paramsJson.periods as Periods),
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.SENIORITY_ENTITLEMENT_DELETED:
          color = 'red'
          text = (
            <IntlMessages
              id={paramsJson.deleteSeniorityEntitlementAddedDays ? 'components.logs.seniorityEntitlementDeletedWithDays' : 'components.logs.seniorityEntitlementDeletedKeepDays'}
              values={{
                user: creatorName,
                name: filterXSS(paramsJson.automationName as string),
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.ENTITLEMENT_BY_ROLE_CREATED:
          color = 'green'
          text = (
            <IntlMessages
              id="components.logs.entitlementByRoleCreated"
              values={{
                user: creatorName,
                name: filterXSS(paramsJson.name as string),
                label: filterXSS(paramsJson.label as string),
                amount: paramsJson.amount,
                units: paramsJson.units === 'hours' ? (paramsJson.amount === 1 ? words.hour : words.hours) : paramsJson.amount === 1 ? words.day : words.days,
                enabled: decorateResponse(paramsJson.isActive),
                leaveType: filterXSS(paramsJson.leaveType as string),
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.ENTITLEMENT_BY_ROLE_UPDATED:
          color = 'green'
          text = (
            <IntlMessages
              id="components.logs.entitlementByRoleUpdated"
              values={{
                user: creatorName,
                name: filterXSS(paramsJson.name as string),
                label: filterXSS(paramsJson.label as string),
                amount: paramsJson.amount,
                units: paramsJson.units === 'hours' ? (paramsJson.amount === 1 ? words.hour : words.hours) : paramsJson.amount === 1 ? words.day : words.days,
                enabled: decorateResponse(paramsJson.isActive),
                leaveType: paramsJson.leaveType,
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.ENTITLEMENT_BY_ROLE_DELETED:
          color = 'red'
          text = (
            <IntlMessages
              id="components.logs.entitlementByRoleDeleted"
              values={{
                user: creatorName,
                name: filterXSS(paramsJson.automationName as string),
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.VISIBILITY_ADDON_CREATED:
          color = 'green'
          text = (
            <IntlMessages
              id="components.logs.visibilityAddonCreated"
              values={{
                user: creatorName,
                enabled: decorateResponse(paramsJson.isActive),
                visibilityLevel: paramsJson.visibilityLevel.join(', ') || 'None',
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.VISIBILITY_ADDON_UPDATED:
          color = 'green'
          text = (
            <IntlMessages
              id="components.logs.visibilityAddonUpdated"
              values={{
                user: creatorName,
                enabled: decorateResponse(paramsJson.isActive),
                visibilityLevel: paramsJson.visibilityLevel.join(', ') || 'None',
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.ADDONS_UPDATED: {
          const addons = getAddonsEvents()
          const currentAddon = addons.find((addon) => addon.type === paramsJson.type)
          const isIncludedInCurrentPlan = currentAddon?.includedIn?.includes(companyPlan ?? '')

          let textId = 'components.logs.automationAddonsUpdatedPurchased'
          if (!paramsJson.isActive) {
            color = 'red'
            textId = 'components.logs.automationAddonsUpdatedUnsubscribed'
          }
          if (isIncludedInCurrentPlan && paramsJson.isActive) {
            textId = 'components.logs.automationAddonsUpdatedEnabled'
          }
          if (isIncludedInCurrentPlan && !paramsJson.isActive) {
            textId = 'components.logs.automationAddonsUpdatedDisabled'
          }

          text = (
            <IntlMessages
              id={textId}
              values={{
                user: creatorName,
                automationType: <IntlMessages id={`automations.${paramsJson.type}`} />,
              }}
            />
          )
          break
        }
        case EventTypeEnum.SUBSTITUTE_APPROVER_CREATED:
          text = (
            <IntlMessages
              id="components.logs.substituteApproverCreated"
              values={{
                creator: filterXSS(creatorName),
                approverFor: filterXSS(paramsJson.approverFor),
                substituteApproverName: filterXSS(paramsJson.substituteApproverName),
                startDate: filterXSS(paramsJson.startDate),
                endDate: filterXSS(paramsJson.endDate),
                br: () => <br />,
              }}
            />
          )
          break
        case EventTypeEnum.SUBSTITUTE_APPROVER_UPDATED:
          text = (
            <IntlMessages
              id="components.logs.substituteApproverUpdated"
              values={{
                creator: filterXSS(creatorName),
                substituteApproverName: filterXSS(paramsJson.substituteApproverName),
                startDate: filterXSS(paramsJson.startDate),
                endDate: filterXSS(paramsJson.endDate),
                br: () => <br />,
              }}
            />
          )
          break
        case EventTypeEnum.SUBSTITUTE_APPROVER_DELETED:
          text = (
            <IntlMessages
              id="components.logs.substituteApproverDeleted"
              values={{
                creator: filterXSS(creatorName),
                substituteApproverName: filterXSS(paramsJson.substituteApproverName),
                startDate: filterXSS(paramsJson.startDate),
                endDate: filterXSS(paramsJson.endDate),
                br: () => <br />,
              }}
            />
          )
          break
        case EventTypeEnum.LEAVE_DURATION_LIMIT_CREATED:
          color = 'green'
          text = (
            <IntlMessages
              id="components.logs.leaveDurationLimitCreated"
              values={{
                user: creatorName,
                name: filterXSS(paramsJson.name as string),
                enabled: decorateResponse(paramsJson.isActive),
                startDate: decorateDate(new Date(paramsJson.period[0] as string)),
                endDate: decorateDate(new Date(paramsJson.period[1] as string)),
                minimumDuration: paramsJson.minimumDuration || <IntlMessages id="app.notSet" />,
                maximumDuration: paramsJson.maximumDuration || <IntlMessages id="app.notSet" />,
                recurring: decorateResponse(paramsJson.recurring),
                units: paramsJson.quotaSetIn === 'hours' ? <IntlMessages id="app.hours" /> : <IntlMessages id="app.days" />,
                autoRejectResponse: paramsJson.autoRejectResponse,
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.LEAVE_DURATION_LIMIT_UPDATED:
          color = 'green'
          text = (
            <IntlMessages
              id="components.logs.leaveDurationLimitUpdated"
              values={{
                user: creatorName,
                name: filterXSS(paramsJson.name as string),
                enabled: decorateResponse(paramsJson.isActive),
                startDate: decorateDate(new Date(paramsJson.period[0] as string)),
                endDate: decorateDate(new Date(paramsJson.period[1] as string)),
                minimumDuration: paramsJson.minimumDuration || <IntlMessages id="app.notSet" />,
                maximumDuration: paramsJson.maximumDuration || <IntlMessages id="app.notSet" />,
                recurring: decorateResponse(paramsJson.recurring),
                units: paramsJson.quotaSetIn === 'hours' ? <IntlMessages id="app.hours" /> : <IntlMessages id="app.days" />,
                autoRejectResponse: paramsJson.autoRejectResponse,
                br: () => <br />,
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.LEAVE_DURATION_LIMIT_DELETED:
          color = 'red'
          text = (
            <IntlMessages
              id="components.logs.leaveDurationLimitDeleted"
              values={{
                user: creatorName,
                name: filterXSS(paramsJson.automationName as string),
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.PUBLIC_API_KEY_CREATED:
        case EventTypeEnum.PUBLIC_API_KEY_UPDATED:
        case EventTypeEnum.PUBLIC_API_KEY_ROTATED:
          text = (
            <IntlMessages
              id={publicApiKeyMessageIds[type]}
              values={{ user: creatorName, name: filterXSS(publicApiKeyLogName(paramsJson)), b: (...chunks) => <Text strong>{chunks}</Text> }}
            />
          )
          break
        case EventTypeEnum.PUBLIC_API_KEY_REVOKED:
          color = 'red'
          text = (
            <IntlMessages
              id={paramsJson.automatic ? 'components.logs.publicApiKeyRevokedAutomatically' : 'components.logs.publicApiKeyRevoked'}
              values={{
                user: creatorName,
                name: filterXSS(publicApiKeyLogName(paramsJson)),
                reason: revocationReasonMessageIds[paramsJson.revocationCode as PublicApiKeyRevocationCode]
                  ? formatMessage({ id: revocationReasonMessageIds[paramsJson.revocationCode as PublicApiKeyRevocationCode] })
                  : filterXSS(paramsJson.revocationReason as string),
                b: (...chunks) => <Text strong>{chunks}</Text>,
              }}
            />
          )
          break
        case EventTypeEnum.PUBLIC_API_WEBHOOK_CREATED:
        case EventTypeEnum.PUBLIC_API_WEBHOOK_UPDATED:
        case EventTypeEnum.PUBLIC_API_WEBHOOK_DELETED:
          if (type === EventTypeEnum.PUBLIC_API_WEBHOOK_DELETED) {
            color = 'red'
          }
          text = (
            <IntlMessages
              id={publicApiWebhookMessageIds[type]}
              values={{ user: creatorName, url: filterXSS(paramsJson.url as string), b: (...chunks) => <Text strong>{chunks}</Text> }}
            />
          )
          break
        default:
          text = type
          break
      }
    } catch (e) {
      text = <IntlMessages id="components.logs.failedToLoadLog" />
    }
  }

  setLogs()

  if (apiClient) {
    const keyName = apiClient.keyName ?? apiClient.name
    const keyLineValues = { keyName: filterXSS(keyName ?? ''), ownerName: filterXSS(apiClient.ownerName) }
    const onBehalfId = keyName ? 'components.logs.publicApiKeyLineOnBehalf' : 'components.logs.publicApiKeyLineUnnamedOnBehalf'
    const ownId = keyName ? 'components.logs.publicApiKeyLine' : 'components.logs.publicApiKeyLineUnnamed'
    text = (
      <>
        {text}
        <br />
        <Text type="secondary" style={{ fontSize: 12 }}>
          <KeyOutlined />{' '}
          {apiClient.ownerId !== creatorId ? (
            <IntlMessages id={onBehalfId} values={{ ...keyLineValues, actingAs: filterXSS(actingAsName) }} />
          ) : (
            <IntlMessages id={ownId} values={keyLineValues} />
          )}
        </Text>
      </>
    )
  }

  return {
    key: `${time}-${index}`,
    color,
    children: text,
    label: time,
  }
}

const decorateUserName = (userName: string, decoratedMessage: string) => {
  return `${userName} ${decoratedMessage}`
}

const getMaximumUsersAwayValues = (paramsJson: any) => {
  return {
    startDate: decorateDate(new Date(paramsJson.startDate as string)),
    endDate: decorateDate(new Date(paramsJson.endDate as string)),
    recurring: decorateResponse(paramsJson.recurring),
    maxUsersAllowed: paramsJson.maxUsersAway.value,
    maxUsersAwayType: paramsJson.maxUsersAway.type,
    autoRejectResponse: paramsJson.autoRejectResponse,
    leaveTypes: paramsJson.leaveTypes?.join(', '),
    locations: Array.isArray(paramsJson.locations) ? paramsJson.locations?.join(', ') : paramsJson.locations,
    teams: Array.isArray(paramsJson.teams) ? paramsJson.teams?.join(', ') : paramsJson.teams,
    labels: Array.isArray(paramsJson.labels) ? paramsJson.labels?.join(', ') : paramsJson.labels,
    name: filterXSS(paramsJson.name as string),
    enabled: decorateResponse(paramsJson.isActive),
  }
}
